import { NavLink } from "react-router-dom";
import styled, { css } from "styled-components";

import { adminNavigation } from "@pages/AdminDashboard/adminNavigation";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";

const { color, radius, typeScale } = adminTokens;
const [, spaceSm, , , spaceLg] = adminTokens.spacing;
const itemPaddingY = spaceSm;
const itemPaddingX = 10;
const groupGap = 2;
// The design's drawer item: at least 52px tall, padded 6px by 12px, its name in
// Medium 15 (SHELL-R37).
const drawerItemMinHeight = 52;
const drawerItemPaddingY = 6;
const drawerItemPaddingX = 12;
const drawerItemNameSize = "15px";

const Group = styled.div.attrs({ role: "group" })`
  display: flex;
  flex-direction: column;
  gap: ${groupGap}px;
`;

const GroupHeading = styled.span<{ $isInDrawer: boolean }>`
  padding: 0 ${({ $isInDrawer }) => ($isInDrawer ? drawerItemPaddingX : itemPaddingX)}px 6px;
  color: ${color.inkSoft};
  font-family: ${typeScale.eyebrow.fontFamily};
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;

  ${Group} + ${Group} & {
    padding-top: ${spaceLg}px;
  }
`;

const drawerItemStyle = css`
  justify-content: center;
  box-sizing: border-box;
  min-height: ${drawerItemMinHeight}px;
  padding: ${drawerItemPaddingY}px ${drawerItemPaddingX}px;
`;

const Item = styled(NavLink)<{ $isInDrawer: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  padding: ${itemPaddingY}px ${itemPaddingX}px;
  border-radius: ${radius.row}px;
  color: ${color.navy};
  text-align: left;
  text-decoration: none;
  ${({ $isInDrawer }) => $isInDrawer && drawerItemStyle}

  &[aria-current="page"] {
    background-color: ${color.canvas};
  }

  &:focus-visible {
    ${focusOutline}
  }
`;

const ItemName = styled.span<{ $isInDrawer: boolean }>`
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${({ $isInDrawer }) =>
    $isInDrawer ? drawerItemNameSize : typeScale.labelValue.fontSize};
`;

const ItemSubtitle = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: 12px;
`;

const toId = (text: string) => `admin-navigation-${text.toLowerCase().replace(/\s+/g, "-")}`;

interface AdminNavigationListProps {
  // "sidebar": the desktop sidebar (SHELL-R4…R9); "drawer": the phone's navigation
  // drawer, with taller items (SHELL-R37).
  placement: "sidebar" | "drawer";
  onNavigate?: () => void;
}

// The admin's navigation, grouped by what the admin is doing (SHELL-R4…R9).
export const AdminNavigationList = ({ placement, onNavigate }: AdminNavigationListProps) => {
  const isInDrawer = placement === "drawer";

  return (
    <nav aria-label="Admin">
      {adminNavigation.map((group) => (
        <Group key={group.heading} aria-labelledby={toId(group.heading)}>
          <GroupHeading id={toId(group.heading)} $isInDrawer={isInDrawer}>
            {group.heading}
          </GroupHeading>
          {group.items.map((item) => (
            <Item
              key={item.label}
              to={item.path}
              exact
              aria-label={item.label}
              aria-describedby={toId(`${item.label}-subtitle`)}
              onClick={onNavigate}
              $isInDrawer={isInDrawer}
            >
              <ItemName $isInDrawer={isInDrawer}>{item.label}</ItemName>
              <ItemSubtitle id={toId(`${item.label}-subtitle`)}>{item.subtitle}</ItemSubtitle>
            </Item>
          ))}
        </Group>
      ))}
    </nav>
  );
};
